import type {
  DataResponse,
  PaginatedResponse,
  ProjectActivity,
  ProjectArtifact,
  ProjectCreate,
  ProjectHub,
  ProjectKnowledgeBaseFile,
  ProjectMemberAdd,
  ProjectMember as ProjectMemberType,
  ProjectResource,
  ProjectResourceAdd,
  ProjectResponse,
  ProjectSummary,
  ProjectUpdate,
} from "@/types";
import { api } from "../api";

export const projectService = {
  list: (params?: { page?: number; pageSize?: number; status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    if (params?.status) searchParams.set("status", params.status);
    return api.get<PaginatedResponse<ProjectSummary>>(`/projects?${searchParams}`);
  },
  get: (projectId: string) => api.get<DataResponse<ProjectResponse>>(`/projects/${projectId}`),
  getHub: (projectId: string) => api.get<DataResponse<ProjectHub>>(`/projects/${projectId}/hub`),
  create: (data: ProjectCreate) => api.post<DataResponse<ProjectResponse>>("/projects", data),
  update: (projectId: string, data: ProjectUpdate) =>
    api.put<DataResponse<ProjectResponse>>(`/projects/${projectId}`, data),
  delete: (projectId: string) => api.delete<{ message: string }>(`/projects/${projectId}`),

  // Members
  listMembers: (projectId: string) => api.get<DataResponse<ProjectMemberType[]>>(`/projects/${projectId}/members`),
  addMember: (projectId: string, data: ProjectMemberAdd) =>
    api.post<DataResponse<ProjectMemberType>>(`/projects/${projectId}/members`, data),
  addTeamMembers: (projectId: string, data: { teamUuid: string; role?: string }) =>
    api.post<DataResponse<ProjectMemberType[]>>(`/projects/${projectId}/members/team`, data),
  updateMember: (projectId: string, userUuid: string, role: string) =>
    api.put<DataResponse<ProjectMemberType>>(`/projects/${projectId}/members/${userUuid}`, { role }),
  removeMember: (projectId: string, userUuid: string) =>
    api.delete<{ message: string }>(`/projects/${projectId}/members/${userUuid}`),

  // Resources
  addResource: (projectId: string, data: ProjectResourceAdd) =>
    api.post<DataResponse<ProjectResource>>(`/projects/${projectId}/resources`, data),
  removeResource: (projectId: string, resourceUuid: string) =>
    api.delete<{ message: string }>(`/projects/${projectId}/resources/${resourceUuid}`),
  reorderResources: (projectId: string, resourceUuids: string[]) =>
    api.put<{ message: string }>(`/projects/${projectId}/resources/reorder`, {
      resourceUuids,
    }),

  // Knowledge Base Files (shortcuts to default data collection)
  uploadFiles: (projectId: string, files: File[]) =>
    api.upload<DataResponse<ProjectKnowledgeBaseFile[]>>(`/projects/${projectId}/files`, files),
  deleteFile: (projectId: string, fileUuid: string) =>
    api.delete<{ message: string }>(`/projects/${projectId}/files/${fileUuid}`),

  // Activities
  getActivities: (projectId: string, params?: { page?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    return api.get<PaginatedResponse<ProjectActivity>>(`/projects/${projectId}/activities?${searchParams}`);
  },

  // Artifacts
  getArtifacts: (projectId: string, params?: { page?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    return api.get<PaginatedResponse<ProjectArtifact>>(`/projects/${projectId}/artifacts?${searchParams}`);
  },
};
